import { useState, useMemo } from 'react';
import { demoRooms, demoBookings, demoGuests, demoRoomBeds, demoBedBookings } from '@/mocks/demoData';
import type { Room, RoomBed } from '@/mocks/data';
import { useApp } from '@/contexts/AppContext';

const CATEGORIES = ['Standard AC', 'Deluxe AC', 'Suite', 'Standard Non-AC', 'Dormitory', 'Shared Room'];
const ROOM_TYPES = ['private', 'shared', 'dormitory'];
const STATUS_COLORS: Record<string, { bg: string; text: string; border: string; icon: string }> = {
  available: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: 'ri-check-line' },
  occupied: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: 'ri-user-line' },
  cleaning: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', icon: 'ri-brush-line' },
  maintenance: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200', icon: 'ri-tools-line' },
};

const BED_STATUS: Record<RoomBed['status'], { bg: string; text: string; label: string }> = {
  available: { bg: 'bg-green-500', text: 'text-white', label: 'Available' },
  occupied: { bg: 'bg-red-500', text: 'text-white', label: 'Occupied' },
  reserved: { bg: 'bg-yellow-500', text: 'text-white', label: 'Reserved' },
  cleaning: { bg: 'bg-gray-400', text: 'text-white', label: 'Cleaning' },
  maintenance: { bg: 'bg-gray-600', text: 'text-white', label: 'Maintenance' },
};

const ROOM_TYPE_LABELS: Record<string, string> = {
  private: 'Private',
  shared: 'Shared',
  dormitory: 'Dormitory',
};

export default function BookingRooms() {
  const { addToast } = useApp();
  const [rooms, setRooms] = useState<Room[]>(demoRooms);
  const [filterFloor, setFilterFloor] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRoomType, setFilterRoomType] = useState('all');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [showBedLayout, setShowBedLayout] = useState<Room | null>(null);
  const [statusDropdown, setStatusDropdown] = useState<string | null>(null);
  const [bedStatusDropdown, setBedStatusDropdown] = useState<string | null>(null);
  const [beds, setBeds] = useState<RoomBed[]>(demoRoomBeds);
  const [formData, setFormData] = useState({
    room_number: '', floor: '', category: 'Standard AC', room_type: 'private' as Room['room_type'],
    tariff: '', per_bed_price: '', total_beds: '1', description: '',
    status: 'available' as Room['status'], max_occupancy: '2',
  });

  const floors = useMemo(() => [...new Set(rooms.map((r) => r.floor))].sort((a, b) => a - b), [rooms]);

  const filteredRooms = useMemo(() => {
    return rooms.filter((r) => {
      if (filterFloor !== 'all' && r.floor !== Number(filterFloor)) return false;
      if (filterCategory !== 'all' && r.category !== filterCategory) return false;
      if (filterStatus !== 'all' && r.status !== filterStatus) return false;
      if (filterRoomType !== 'all' && r.room_type !== filterRoomType) return false;
      if (search && !r.room_number.toLowerCase().includes(search.toLowerCase()) && !r.description.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [rooms, filterFloor, filterCategory, filterStatus, filterRoomType, search]);

  const statusCounts = useMemo(() => {
    return {
      available: rooms.filter((r) => r.status === 'available').length,
      occupied: rooms.filter((r) => r.status === 'occupied').length,
      cleaning: rooms.filter((r) => r.status === 'cleaning').length,
      maintenance: rooms.filter((r) => r.status === 'maintenance').length,
    };
  }, [rooms]);

  const getBedsForRoom = (roomId: string) => beds.filter((b) => b.room_id === roomId);
  const getBedStats = (roomId: string) => {
    const roomBeds = getBedsForRoom(roomId);
    return {
      total: roomBeds.length,
      occupied: roomBeds.filter((b) => b.status === 'occupied').length,
      reserved: roomBeds.filter((b) => b.status === 'reserved').length,
      available: roomBeds.filter((b) => b.status === 'available').length,
      cleaning: roomBeds.filter((b) => b.status === 'cleaning').length,
      maintenance: roomBeds.filter((b) => b.status === 'maintenance').length,
    };
  };

  const openAdd = () => {
    setEditingRoom(null);
    setFormData({ room_number: '', floor: '', category: 'Standard AC', room_type: 'private', tariff: '', per_bed_price: '', total_beds: '1', description: '', status: 'available', max_occupancy: '2' });
    setShowForm(true);
  };

  const openEdit = (room: Room) => {
    setEditingRoom(room);
    setFormData({
      room_number: room.room_number,
      floor: String(room.floor),
      category: room.category,
      room_type: room.room_type,
      tariff: String(room.tariff),
      per_bed_price: String(room.per_bed_price),
      total_beds: String(room.total_beds),
      description: room.description,
      status: room.status,
      max_occupancy: String(room.max_occupancy),
    });
    setShowForm(true);
  };

  const handleSave = () => {
    if (!formData.room_number || !formData.floor || !formData.tariff) {
      addToast('error', 'Room number, floor, and tariff are required');
      return;
    }
    const totalBeds = (formData.room_type === 'dormitory' || formData.room_type === 'shared') ? Number(formData.total_beds) : 1;
    if (editingRoom) {
      setRooms((prev) => prev.map((r) => (r.id === editingRoom.id ? {
        ...r,
        room_number: formData.room_number,
        floor: Number(formData.floor),
        category: formData.category,
        room_type: formData.room_type,
        tariff: Number(formData.tariff),
        per_bed_price: Number(formData.per_bed_price) || 0,
        total_beds: totalBeds,
        description: formData.description,
        status: formData.status,
        max_occupancy: Number(formData.max_occupancy),
      } : r)));
      addToast('success', `Room ${formData.room_number} updated`);
    } else {
      const newRoom: Room = {
        id: `room-${Date.now()}`,
        room_number: formData.room_number,
        floor: Number(formData.floor),
        category: formData.category,
        room_type: formData.room_type,
        tariff: Number(formData.tariff),
        per_bed_price: Number(formData.per_bed_price) || 0,
        total_beds: totalBeds,
        description: formData.description,
        photos: [],
        status: formData.status,
        max_occupancy: Number(formData.max_occupancy),
      };
      setRooms((prev) => [...prev, newRoom]);
      addToast('success', `Room ${formData.room_number} added`);
    }
    setShowForm(false);
  };

  const handleDelete = (roomId: string) => {
    const hasBooking = demoBookings.some((b) => b.room_id === roomId && ['checked_in', 'confirmed'].includes(b.status));
    if (hasBooking) {
      addToast('error', 'Cannot delete room with active bookings');
      return;
    }
    if (confirm('Are you sure you want to delete this room?')) {
      setRooms((prev) => prev.filter((r) => r.id !== roomId));
      addToast('success', 'Room deleted');
    }
  };

  const changeRoomStatus = (room: Room, newStatus: Room['status']) => {
    if (newStatus === room.status) return;
    setRooms((prev) => prev.map((r) => (r.id === room.id ? { ...r, status: newStatus } : r)));
    setStatusDropdown(null);
    addToast('success', `Room ${room.room_number} status: ${room.status} → ${newStatus}`);
  };

  const changeBedStatus = (bed: RoomBed, newStatus: RoomBed['status']) => {
    if (newStatus === bed.status) return;
    setBeds((prev) => prev.map((b) => (b.id === bed.id ? { ...b, status: newStatus } : b)));
    setBedStatusDropdown(null);
    addToast('success', `Bed ${bed.bed_number} status: ${bed.status} → ${newStatus}`);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-navy-800">Room Setup & Status</h1>
          <p className="text-navy-500 mt-1">Manage rooms, categories, bed layouts and live occupancy status.</p>
        </div>
        <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2.5 bg-navy-700 text-white rounded-lg hover:bg-navy-800 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-add-line" /> Add Room
        </button>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {(['available', 'occupied', 'cleaning', 'maintenance'] as const).map((s) => {
          const colors = STATUS_COLORS[s];
          return (
            <div key={s} className={`rounded-xl border ${colors.border} ${colors.bg} p-5`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-medium capitalize ${colors.text}`}>{s}</span>
                <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${colors.bg}`}>
                  <i className={`${colors.icon} ${colors.text}`} />
                </span>
              </div>
              <div className={`text-2xl font-bold ${colors.text}`}>{statusCounts[s]}</div>
              <div className="text-xs text-navy-400 mt-1">{Math.round((statusCounts[s] / rooms.length) * 100)}% of total</div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-navy-500 mb-1.5">Search</label>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
              <input type="text" placeholder="Search room number or description..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-navy-500 mb-1.5">Floor</label>
            <select value={filterFloor} onChange={(e) => setFilterFloor(e.target.value)} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
              <option value="all">All Floors</option>
              {floors.map((f) => <option key={f} value={f}>Floor {f}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-navy-500 mb-1.5">Category</label>
            <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
              <option value="all">All Categories</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-navy-500 mb-1.5">Room Type</label>
            <select value={filterRoomType} onChange={(e) => setFilterRoomType(e.target.value)} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
              <option value="all">All Types</option>
              {ROOM_TYPES.map((t) => <option key={t} value={t}>{ROOM_TYPE_LABELS[t]}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-navy-500 mb-1.5">Status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
              <option value="all">All Status</option>
              <option value="available">Available</option>
              <option value="occupied">Occupied</option>
              <option value="cleaning">Cleaning</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>
        </div>
      </div>

      {/* Room Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredRooms.map((room) => {
          const colors = STATUS_COLORS[room.status];
          const activeBooking = demoBookings.find((b) => b.room_id === room.id && b.status === 'checked_in');
          const isSharedOrDorm = room.room_type === 'shared' || room.room_type === 'dormitory';
          const bedStats = isSharedOrDorm ? getBedStats(room.id) : null;
          return (
            <div key={room.id} className={`bg-white rounded-xl border ${colors.border} overflow-hidden hover:shadow-md transition-shadow`}>
              <div className={`px-4 py-3 ${colors.bg} border-b ${colors.border} flex items-center justify-between`}>
                <div className="flex items-center gap-2">
                  <span className={`text-lg font-bold ${colors.text}`}>{room.room_number}</span>
                  <span className="text-xs text-navy-400">Floor {room.floor}</span>
                </div>
                <div className="flex items-center gap-2">
                  {room.status === 'occupied' && (
                    <button
                      onClick={() => changeRoomStatus(room, 'cleaning')}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 hover:bg-yellow-200 transition-colors cursor-pointer whitespace-nowrap"
                      title="Send room for cleaning"
                    >
                      <i className="ri-brush-line" /> Clean
                    </button>
                  )}
                  {room.status === 'cleaning' && (
                    <button
                      onClick={() => changeRoomStatus(room, 'available')}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 transition-colors cursor-pointer whitespace-nowrap"
                      title="Mark room as ready"
                    >
                      <i className="ri-check-line" /> Ready
                    </button>
                  )}
                  {room.status === 'maintenance' && (
                    <button
                      onClick={() => changeRoomStatus(room, 'available')}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 transition-colors cursor-pointer whitespace-nowrap"
                      title="Mark as available"
                    >
                      <i className="ri-check-line" /> Release
                    </button>
                  )}
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium capitalize ${colors.bg} ${colors.text}`}>
                    <i className={colors.icon} /> {room.status}
                  </span>
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-sm font-medium text-navy-800">{room.category}</div>
                  {room.room_type !== 'private' && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-gold-100 text-gold-700 font-medium">
                      {ROOM_TYPE_LABELS[room.room_type]}
                    </span>
                  )}
                </div>
                <div className="text-xs text-navy-500 mb-3">{room.description}</div>

                {/* Bed Stats for Shared/Dormitory */}
                {isSharedOrDorm && bedStats && (
                  <div className="mb-3 p-3 bg-navy-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-navy-600">Beds</span>
                      <button
                        onClick={() => setShowBedLayout(room)}
                        className="text-xs text-navy-500 hover:text-navy-700 underline cursor-pointer"
                      >
                        View Layout
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-1 text-center text-xs">
                      <div>
                        <div className="font-semibold text-navy-800">{bedStats.total}</div>
                        <div className="text-navy-400">Total</div>
                      </div>
                      <div>
                        <div className="font-semibold text-red-600">{bedStats.occupied}</div>
                        <div className="text-navy-400">Occupied</div>
                      </div>
                      <div>
                        <div className="font-semibold text-green-600">{bedStats.available}</div>
                        <div className="text-navy-400">Available</div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-xs text-navy-400">Tariff</div>
                    <div className="text-sm font-semibold text-navy-700">
                      {isSharedOrDorm ? (
                        <span>Rs. {room.per_bed_price.toLocaleString('en-IN')}<span className="text-xs font-normal text-navy-400"> /bed</span></span>
                      ) : (
                        <span>Rs. {room.tariff.toLocaleString('en-IN')}</span>
                      )}
                    </div>
                    {isSharedOrDorm && (
                      <div className="text-xs text-navy-400 mt-0.5">Full: Rs. {room.tariff.toLocaleString('en-IN')}</div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-navy-400">Max Guests</div>
                    <div className="text-sm font-semibold text-navy-700">{room.max_occupancy}</div>
                  </div>
                </div>
                {activeBooking && (
                  <div className="mb-3 p-2 bg-navy-50 rounded-lg text-xs text-navy-600">
                    <i className="ri-user-line mr-1" />
                    Occupied by {demoGuests.find((g) => g.id === activeBooking.guest_id)?.full_name || 'Guest'}
                  </div>
                )}
                <div className="flex gap-2">
                  <button onClick={() => openEdit(room)} className="flex-1 px-3 py-2 text-xs font-medium text-navy-700 bg-navy-50 rounded-lg hover:bg-navy-100 transition-colors cursor-pointer">
                    <i className="ri-edit-line mr-1" /> Edit
                  </button>
                  {isSharedOrDorm && (
                    <button onClick={() => setShowBedLayout(room)} className="px-3 py-2 text-xs font-medium text-gold-700 bg-gold-50 rounded-lg hover:bg-gold-100 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-hotel-bed-line mr-1" /> Beds
                    </button>
                  )}
                  <div className="relative">
                    <button
                      onClick={() => setStatusDropdown(statusDropdown === room.id ? null : room.id)}
                      className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${colors.bg} ${colors.text} border ${colors.border}`}
                    >
                      <i className="ri-arrow-left-right-line mr-1" /> Status
                    </button>
                    {statusDropdown === room.id && (
                      <>
                        <div className="fixed inset-0 z-30" onClick={() => setStatusDropdown(null)} />
                        <div className="absolute right-0 bottom-full mb-1 z-40 bg-white rounded-lg border border-navy-100 shadow-lg py-1 w-36">
                          {(['available', 'occupied', 'cleaning', 'maintenance'] as const).map((s) => {
                            const sc = STATUS_COLORS[s];
                            return (
                              <button
                                key={s}
                                onClick={() => changeRoomStatus(room, s)}
                                className={`w-full text-left px-3 py-2 text-xs font-medium flex items-center gap-2 hover:bg-navy-50 transition-colors cursor-pointer capitalize ${room.status === s ? 'bg-navy-50' : ''}`}
                              >
                                <span className={`w-2 h-2 rounded-full ${sc.bg} border ${sc.border}`} />
                                <span className={sc.text}>{s}</span>
                                {room.status === s && <i className="ri-check-line text-xs ml-auto text-green-600" />}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                  <button onClick={() => handleDelete(room.id)} className="px-3 py-2 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors cursor-pointer">
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bed Layout Popup */}
      {showBedLayout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-navy-800">Room {showBedLayout.room_number} — Bed Layout</h3>
                <p className="text-xs text-navy-500">
                  {showBedLayout.total_beds} Beds | {ROOM_TYPE_LABELS[showBedLayout.room_type]} | 
                  <span className="text-green-600 ml-1">{getBedsForRoom(showBedLayout.id).filter(b => b.status === 'available').length} avail</span>
                  {' · '}
                  <span className="text-red-500">{getBedsForRoom(showBedLayout.id).filter(b => b.status === 'occupied').length} occ</span>
                </p>
              </div>
              <button onClick={() => { setShowBedLayout(null); setBedStatusDropdown(null); }} className="text-navy-400 hover:text-navy-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="p-6">
              {(() => {
                const beds = getBedsForRoom(showBedLayout.id);
                if (beds.length === 0) {
                  return <div className="text-center py-8 text-navy-400 text-sm">No bed data available for this room.</div>;
                }
                const cols = Math.min(5, Math.max(2, Math.ceil(beds.length / 2)));
                return (
                  <>
                    <div className="grid gap-3 mb-6" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
                      {beds.sort((a, b) => a.bed_number - b.bed_number).map((bed) => {
                        const bs = BED_STATUS[bed.status];
                        const isDropdownOpen = bedStatusDropdown === bed.id;
                        return (
                          <div key={bed.id} className="relative">
                            <button
                              onClick={() => setBedStatusDropdown(isDropdownOpen ? null : bed.id)}
                              className={`w-full ${bs.bg} ${bs.text} rounded-xl p-3 text-center transition-all cursor-pointer hover:scale-105 active:scale-95`}
                              title={`Bed ${bed.bed_number}: ${bs.label} — Click to change`}
                            >
                              <i className="ri-hotel-bed-line text-xl mb-1 block" />
                              <div className="text-sm font-bold">Bed {bed.bed_number}</div>
                              <div className="text-xs opacity-80 mt-0.5">{bs.label}</div>
                            </button>
                            {isDropdownOpen && (
                              <>
                                <div className="fixed inset-0 z-30" onClick={() => setBedStatusDropdown(null)} />
                                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 z-40 bg-white rounded-lg border border-navy-100 shadow-lg py-1 w-36">
                                  {(Object.keys(BED_STATUS) as RoomBed['status'][]).map((s) => {
                                    const sc = BED_STATUS[s];
                                    return (
                                      <button
                                        key={s}
                                        onClick={() => changeBedStatus(bed, s)}
                                        className={`w-full text-left px-3 py-2 text-xs font-medium flex items-center gap-2 hover:bg-navy-50 transition-colors cursor-pointer capitalize ${bed.status === s ? 'bg-navy-50' : ''}`}
                                      >
                                        <span className={`w-2 h-2 rounded-full ${sc.bg}`} />
                                        <span className="text-navy-700">{sc.label}</span>
                                        {bed.status === s && <i className="ri-check-line text-xs ml-auto text-green-600" />}
                                      </button>
                                    );
                                  })}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex flex-wrap gap-3 justify-center">
                      {Object.entries(BED_STATUS).map(([key, val]) => (
                        <div key={key} className="flex items-center gap-1.5 text-xs">
                          <span className={`w-3 h-3 rounded-full ${val.bg}`} />
                          <span className="text-navy-600 capitalize">{val.label}</span>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
            <div className="px-6 py-4 border-t border-navy-100">
              <button onClick={() => { setShowBedLayout(null); setBedStatusDropdown(null); }} className="w-full px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
              <h3 className="font-semibold text-navy-800">{editingRoom ? 'Edit Room' : 'Add New Room'}</h3>
              <button onClick={() => setShowForm(false)} className="text-navy-400 hover:text-navy-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">Room Number *</label>
                  <input type="text" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.room_number} onChange={(e) => setFormData((p) => ({ ...p, room_number: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">Floor *</label>
                  <input type="number" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.floor} onChange={(e) => setFormData((p) => ({ ...p, floor: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">Room Type</label>
                  <select className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.room_type} onChange={(e) => setFormData((p) => ({ ...p, room_type: e.target.value as Room['room_type'] }))}>
                    {ROOM_TYPES.map((t) => <option key={t} value={t}>{ROOM_TYPE_LABELS[t]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">Category</label>
                  <select className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.category} onChange={(e) => setFormData((p) => ({ ...p, category: e.target.value }))}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">Tariff (Rs.) *</label>
                  <input type="number" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.tariff} onChange={(e) => setFormData((p) => ({ ...p, tariff: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">Status</label>
                  <select className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.status} onChange={(e) => setFormData((p) => ({ ...p, status: e.target.value as Room['status'] }))}>
                    <option value="available">Available</option>
                    <option value="occupied">Occupied</option>
                    <option value="cleaning">Cleaning</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>
              </div>
              {(formData.room_type === 'shared' || formData.room_type === 'dormitory') && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-gold-50 rounded-lg border border-gold-200">
                  <div>
                    <label className="block text-sm font-medium text-navy-700 mb-1.5">Total Beds</label>
                    <input type="number" min="2" max="50" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.total_beds} onChange={(e) => setFormData((p) => ({ ...p, total_beds: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy-700 mb-1.5">Per Bed Price (Rs.)</label>
                    <input type="number" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.per_bed_price} onChange={(e) => setFormData((p) => ({ ...p, per_bed_price: e.target.value }))} />
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">Max Occupancy</label>
                  <input type="number" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.max_occupancy} onChange={(e) => setFormData((p) => ({ ...p, max_occupancy: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Description</label>
                <textarea rows={2} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 resize-none" value={formData.description} onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))} />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-navy-100 flex gap-3">
              <button onClick={() => setShowForm(false)} className="flex-1 px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer">Cancel</button>
              <button onClick={handleSave} className="flex-1 px-4 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer">Save Room</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}